import { Grid, HStack, VStack } from "@chakra-ui/layout";
import {
  FlexProps,
  FormHelperText,
  FormLabel,
  GridProps,
  Switch,
} from "@chakra-ui/react";
import { useFlags } from "flagsmith/react";
import { Field, FieldProps, useFormikContext } from "formik";
import { FormControl, InputControl, SelectControl } from "formik-chakra-ui";
import { useMemo } from "react";
import { MultiSelectInput } from "src/components/Inputs/MultiSelectInput";
import {
  isEleveatedSchoolAdmin,
  isSchoolAdminVariaty,
  SchoolType,
  TeamMemberFormType,
  TeamType,
  UserGroupType,
} from "src/schemas/TeamMember";

type InputSwitchProps = {
  name: string;
};

const InputSwitch: React.FC<InputSwitchProps> = ({ name }) => {
  return (
    <Field name={name}>
      {({ form, field }: FieldProps<number, unknown>) => (
        <Switch
          size="md"
          alignSelf="start"
          {...field}
          id={name}
          value={field.value}
          isChecked={!!field.value}
          onChange={(event) => {
            form.setFieldValue(name, event.target.checked);
          }}
        />
      )}
    </Field>
  );
};

type Props = {
  allSchools: SchoolType[];
  userGroups: UserGroupType[];
  teams: TeamType[];
  disableEmail?: boolean;
  disableRole?: boolean;
  showTeam?: boolean;
  active?: boolean;
} & FlexProps &
  GridProps;

export const TeamMemberForm: React.FC<Props> = ({
  allSchools,
  userGroups,
  teams,
  disableEmail = false,
  disableRole = false,
  active = true,
  ...props
}) => {
  const flags = useFlags(["current-applying-schools"]);

  const formik = useFormikContext<TeamMemberFormType>();
  const selectedUserGroup = useMemo(() => {
    return userGroups.find((ug) => ug.id === formik.values.user_group_id);
  }, [formik.values.user_group_id, userGroups]);

  const selectedSchools = useMemo(
    () =>
      allSchools.flatMap((school) => {
        if (formik.values.selected_school_ids.includes(school.id)) {
          return [school];
        }
        return [];
      }),
    [allSchools, formik.values.selected_school_ids]
  );

  return (
    <Grid templateColumns={"repeat(2, 1fr)"} gap={8}>
      <InputControl
        inputProps={{
          placeholder: "e.g. John",
        }}
        w="100%"
        id="first_name"
        name="first_name"
        label="First Name"
        isRequired
      />
      <InputControl
        inputProps={{
          placeholder: "e.g. Smith",
        }}
        w="100%"
        id="last_name"
        name="last_name"
        label="Last Name"
        isRequired
      />
      <InputControl
        inputProps={{
          type: "email",
          placeholder: "e.g. foo@example.com",
        }}
        w="100%"
        id="email_address"
        name="email_address"
        label="Email Address"
        isRequired
        isDisabled={disableEmail}
      />
      <SelectControl
        label="Role"
        selectProps={{ placeholder: "Select a role" }}
        name="user_group_id"
        isRequired
        isDisabled={disableRole}
      >
        {userGroups.map((group) => (
          <option key={group.id} value={group.id}>
            {group.label}
          </option>
        ))}
      </SelectControl>
      {active ? (
        <SelectControl
          isDisabled={!active}
          selectProps={{ placeholder: "Select a team" }}
          label="Team"
          name="person_team_id"
        >
          {teams.map((team) => (
            <option value={team.id}>{team.name}</option>
          ))}
        </SelectControl>
      ) : null}
      <div></div>
      {selectedUserGroup !== undefined &&
        isSchoolAdminVariaty(selectedUserGroup) && (
          <>
            <FormControl name="assigned_schools">
              <FormLabel htmlFor="assigned_schools">Assigned to:</FormLabel>
              <MultiSelectInput<SchoolType>
                variant="tag"
                size="md"
                options={allSchools || []}
                initialValues={selectedSchools}
                onChange={async (schools) => {
                  await formik.setFieldValue(
                    "selected_school_ids",
                    schools.map((s) => s.id)
                  );
                }}
                labelAttr="name"
              />
            </FormControl>
            <div></div>
            {flags["current-applying-schools"].enabled &&
              selectedUserGroup !== undefined &&
              isEleveatedSchoolAdmin(selectedUserGroup) && (
                <FormControl name="access_to" gridColumn="span 2">
                  <FormLabel htmlFor="access_to" marginBottom="1rem">
                    Access to:
                  </FormLabel>
                  <HStack>
                    <InputSwitch name="enable_attending_schools" />
                    <VStack alignItems="start" gap={0} marginLeft="0.25rem">
                      <FormLabel htmlFor="assigned_schools" marginBottom={0}>
                        Current Students
                      </FormLabel>
                      <FormHelperText marginTop="0.25rem" whiteSpace={"nowrap"}>
                        This member can see students whose current school
                        matches the one the member is assigned to
                      </FormHelperText>
                    </VStack>
                  </HStack>
                  <HStack marginTop="1rem">
                    <InputSwitch name="enable_applying_schools" />
                    <VStack alignItems="start" gap={0} marginLeft="0.25rem">
                      <FormLabel htmlFor="assigned_schools" marginBottom={0}>
                        Applying Students
                      </FormLabel>
                      <FormHelperText marginTop="0.25rem" whiteSpace={"nowrap"}>
                        This member can see students applying to the school the
                        member is assigned to
                      </FormHelperText>
                    </VStack>
                  </HStack>
                </FormControl>
              )}
          </>
        )}
    </Grid>
  );
};
